import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@shared/components/ui/form';
import { Input, InputProps } from '@shared/components/ui/input';
import { cn } from '@shared/lib/utils';
import { ClassValue } from 'clsx';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import Unit, { UnitVariant } from '../content/unit';
import InputLabel from './input-label';
import { FormProps } from './types';

type InputFormFieldProps = InputProps &
  FormProps & {
    inputClassName?: ClassValue;
    adornmentRight?: string | React.ReactNode;
    unit?: UnitVariant;
    optional?: boolean;
    enable1P?: boolean;
    adornmentWidth?: number;
  };
export default function InputFormField({
  name,
  className,
  label,
  includeErrorMessage = false,
  inputClassName,
  adornmentRight,
  optional = false,
  unit,
  enable1P = false,
  adornmentWidth,
  tooltip,
  ...props
}: InputFormFieldProps) {
  const { control } = useFormContext();
  const adornmentRef = React.useRef<HTMLDivElement | null>(null);
  const [measuredAdornmentWidth, setMeasuredAdornmentWidth] = React.useState<
    number | null
  >(null);

  React.useLayoutEffect(() => {
    if (adornmentWidth != null) return;
    const el = adornmentRef.current;
    if (!el) {
      setMeasuredAdornmentWidth(null);
      return;
    }
    const update = () => setMeasuredAdornmentWidth(el.offsetWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [adornmentRight, adornmentWidth]);

  const paddingRightPx =
    adornmentWidth != null
      ? adornmentWidth + 20
      : measuredAdornmentWidth != null
        ? measuredAdornmentWidth + 20
        : null;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className={cn('grid w-full items-start', className)}>
          {label && (
            <InputLabel
              label={label}
              name={name}
              optional={optional}
              tooltip={tooltip}
            />
          )}
          <div className="flex flex-row items-center space-x-2">
            <div className="relative flex-1">
              <FormControl>
                <Input
                  className={cn(
                    'w-full placeholder:text-gray-300',
                    {
                      'border-destructive': fieldState.error,
                      'pr-9': !!adornmentRight && paddingRightPx == null,
                    },
                    inputClassName
                  )}
                  style={{
                    paddingRight:
                      paddingRightPx != null
                        ? `${paddingRightPx}px`
                        : undefined,
                  }}
                  {...field}
                  {...props}
                  data-1p-ignore={!enable1P}
                />
              </FormControl>
              {adornmentRight && (
                <div
                  ref={adornmentRef}
                  className={cn(
                    'absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground',
                    {
                      'justify-end': adornmentWidth != null,
                    }
                  )}
                  style={{ width: adornmentWidth }}
                >
                  {adornmentRight}
                </div>
              )}
            </div>
            {unit && <Unit variant={unit} className="text-xs" />}
          </div>
          {includeErrorMessage && <FormMessage />}
        </FormItem>
      )}
    />
  );
}
